import BigNumber from 'bignumber.js';
import { useTranslation } from 'react-i18next';
import { useContext, useMemo } from 'react';

import { UniTextDetails } from '@/design-system/components/cards/UniTextDetailsCard';
import { formatDate } from '../../../../packages/utils/dateFormatting';
import { formatAmountByDecimals } from '../../../../packages/utils/numberFormatting';
import { useUtxEarnSubscriptionsRedeem } from '@utx/utx-client';
import { CurrenciesContext } from '@/currencies/currency.context';

interface UseExecuteRedemptionParams {
  assetSubscribedId: string;
  isFlexible: boolean;
  isMax?: boolean;
  redemptionAmount?: string;
}

export type ExecuteRedemptionResponse = {
  id: string;
  offerId: string;
  amount: number;
  asset: string;
  yieldAsset: string;
  redemptionDate: string;
  refId: string;
  fee?: number;
};

export const useExecuteRedemption = (props: UseExecuteRedemptionParams) => {
  const { t } = useTranslation('earn');
  const { getDecimalByAsset } = useContext(CurrenciesContext);
  const { assetSubscribedId, redemptionAmount } = props;

  const execute = useUtxEarnSubscriptionsRedeem();

  const textDetailsCardData = useMemo(() => {
    let result: Array<UniTextDetails> = [];
    if (!execute.data) return result;
    const data = execute.data.data as ExecuteRedemptionResponse;
    result = [
      {
        name: t('earn-invest::glossary.redemption-amount'),
        data: formatAmountByDecimals({ value: data.amount, decimals: getDecimalByAsset(data.asset), suffix: data.asset })
      },
      {
        name: t('earn-invest::glossary.instruction-time'),
        data: formatDate(data.redemptionDate)
      },
      {
        name: t('earn-invest::glossary.ref-id'),
        data: data.refId
      }
    ];

    if (data.fee) {
      result.splice(1, 0, {
        name: t('earn-invest::glossary.early-fee'),
        data: data.fee ? formatAmountByDecimals({ value: data.fee, decimals: getDecimalByAsset(data.asset), suffix: data.asset }) : ' - '
      });
    }

    return result;
  }, [execute.data]);

  const redeem = () => {
    let data = {
      subscriptionId: assetSubscribedId
    };

    if (props.isFlexible) {
      data = { ...data, amount: new BigNumber(redemptionAmount || 0).toNumber() };
    }

    if (props.isMax) {
      data = { ...data, isMax: true };
    }

    execute.mutate({
      data
    });
  };

  return {
    ...execute,
    textDetailsCardData,
    redeem
  };
};
